import React, { useState, useRef } from 'react';
import {
  Box, Paper, Typography, Button, TextField, MenuItem, Stack, Chip,
  CircularProgress, Alert, LinearProgress, Collapse, Switch, FormControlLabel, Divider, Link, Autocomplete,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SendIcon from '@mui/icons-material/Send';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson } from '../../utils/apiFout';

const COMMON_ROLES = ['Editor', 'Animator', 'Illustrator', 'Camera', 'Geluid', 'Motion designer', 'Regie', 'Producer'];

const isoDaysFromNow = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const capColor = (pct) => (pct >= 100 ? '#ef4444' : pct >= 80 ? '#f59e0b' : '#16a34a');

// Verschuifbaar = geen harde deadline in de periode én niet Prio 1, en echt werk (geen draaidag/vrij).
const NON_WORK = new Set(['Draaidag', 'Vrij/intern', 'Vrije dag', 'Afspraak', 'Vanallesuurtje']);
const isFlexibleTask = (t, endDate) =>
  (!t.deadline || t.deadline > endDate) && t.status !== 'Prio 1' && !NON_WORK.has(t.type);

// Greedy verdeling; met verschuifbaar erbij als useFlexible aanstaat.
function distribute(cands, totalHours, useFlexible) {
  const capOf = (c) => Math.max(0, c.availableHours) + (useFlexible ? Math.max(0, c.flexibleHours || 0) : 0);
  const sorted = [...cands].sort((a, b) => capOf(b) - capOf(a));
  let remaining = totalHours;
  const alloc = [];
  for (const c of sorted) {
    if (remaining <= 0) break;
    const free = capOf(c);
    const give = Math.min(remaining, free);
    if (give > 0) {
      alloc.push({ ...c, assigned: Math.round(give * 10) / 10, needsShift: give > Math.max(0, c.availableHours) });
      remaining -= give;
    }
  }
  return { alloc, shortfall: Math.max(0, Math.round(remaining * 10) / 10) };
}

export default function SlimmeToewijzing() {
  const [role, setRole] = useState('Editor');
  const [hours, setHours] = useState('');
  const [deadline, setDeadline] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // { candidates, totalHours, startDate, endDate }
  const [useFlexible, setUseFlexible] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [tasksById, setTasksById] = useState({}); // { id: { loading, tasks, error } }
  // Project koppelen (optioneel) + chat-vraag.
  const [project, setProject] = useState(null);
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectSearching, setProjectSearching] = useState(false);
  const projTimer = useRef(null);
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [chatReply, setChatReply] = useState(null);

  const searchProjects = (value) => {
    clearTimeout(projTimer.current);
    if (!value || value.length < 2) { setProjectOptions([]); return; }
    projTimer.current = setTimeout(async () => {
      setProjectSearching(true);
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/gripp/projects?search=${encodeURIComponent(value)}&activeOnly=true`);
        const d = await leesJson(r, 'Projecten zoeken');
        setProjectOptions((d.projects || []).map((p) => ({
          id: p.id, number: p.number || p.id,
          label: `${p.searchname || p.additionalsubject || `Project ${p.number}`}${p.company?.searchname ? ` — ${p.company.searchname}` : ''}`,
        })));
      } catch (e) { setProjectOptions([]); setError(e.message); }
      finally { setProjectSearching(false); }
    }, 400);
  };

  const search = async (over = {}) => {
    const useRole = over.role ?? role;
    const useHours = over.hours ?? hours;
    const useDeadline = over.deadline ?? deadline;
    if (!useRole.trim()) return;
    const startDate = isoDaysFromNow(0);
    const endDate = useDeadline && useDeadline >= startDate ? useDeadline : isoDaysFromNow(14);
    const totalHours = Number(useHours) > 0 ? Number(useHours) : null;
    setLoading(true); setError(null); setResult(null); setExpandedId(null); setTasksById({});
    try {
      const qs = `roles[]=${encodeURIComponent(useRole.trim())}&startDate=${startDate}&endDate=${endDate}`;
      const r = await authFetch(`${BACKEND_URL}/Api/capacity-by-role?${qs}`);
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Zoeken mislukt');
      const matched = (data[useRole.trim()] || []).filter((c) => c._matched);
      setResult({ candidates: matched, totalHours, startDate, endDate, role: useRole, project: over.project ?? project });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // 💬 Chat: vrije vraag → functie/uren/deadline/project → automatisch zoeken.
  const askChat = async () => {
    const q = chatInput.trim();
    if (!q || chatBusy) return;
    setChatBusy(true); setError(null); setChatReply(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/toewijzing/parse`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ question: q }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Kon de vraag niet verwerken');
      if (d.role) setRole(d.role);
      setHours(d.hours ? String(d.hours) : '');
      setDeadline(d.deadline || '');
      setChatReply(d.reply);
      // Project uit de vraag proberen te matchen (eerste zoekresultaat).
      let proj = null;
      if (d.projectName) {
        try {
          const pr = await authFetch(`${BACKEND_URL}/Api/gripp/projects?search=${encodeURIComponent(d.projectName)}&activeOnly=true`);
          const pd = await leesJson(pr, 'Project zoeken');
          const first = (pd.projects || [])[0];
          if (first) {
            proj = { id: first.id, number: first.number || first.id, label: `${first.searchname || `Project ${first.number}`}${first.company?.searchname ? ` — ${first.company.searchname}` : ''}` };
            setProject(proj); setProjectOptions([proj]);
          }
        } catch { /* bewust stil: project uit de vraag is een extra — zonder match kiest de gebruiker zelf */ }
      }
      await search({ role: d.role, hours: d.hours ? String(d.hours) : '', deadline: d.deadline || '', project: proj ?? project });
    } catch (e) { setError(e.message); }
    finally { setChatBusy(false); }
  };

  const toggleExpand = async (c) => {
    const next = expandedId === c.id ? null : c.id;
    setExpandedId(next);
    if (next && !tasksById[c.id]) {
      setTasksById((m) => ({ ...m, [c.id]: { loading: true, tasks: [] } }));
      try {
        const params = `startDate=${result.startDate}&endDate=${result.endDate}&personIds[]=${c.id}`;
        const r = await authFetch(`${BACKEND_URL}/Api/person-tasks-range?${params}`);
        const data = await leesJson(r, 'Taken laden');
        setTasksById((m) => ({ ...m, [c.id]: { loading: false, tasks: (data[c.id] || []) } }));
      } catch (e) {
        setTasksById((m) => ({ ...m, [c.id]: { loading: false, tasks: [], error: e.message } }));
      }
    }
  };

  const distribution = result?.totalHours ? distribute(result.candidates, result.totalHours, useFlexible) : null;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
        <GroupsIcon sx={{ color: '#0f766e' }} />
        <Typography variant="h5" sx={{ fontWeight: 800 }}>Slimme toewijzing</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Wie kan werk oppakken? Kies een functie — vul optioneel uren en een deadline in, dan stel ik
        een verdeling voor over de mensen met de meeste ruimte. Tik op iemand voor de details. Voorstel; je wijst zelf toe.
      </Typography>
      <Alert severity="info" icon={false} sx={{ mb: 2.5, py: 0.5 }}>
        <Typography variant="caption">
          Regels: ruimte = 8u/werkdag min taken + agenda-meetings; verschuifbaar = taken zonder harde deadline (niet Prio 1);
          een vanallesuurtje telt niet mee op een draaidag. Aanpassen kan bij{' '}
          <Link href="/admin/settings?tab=app&section=planning-regels" sx={{ fontWeight: 700 }}>Admin → Planning-regels</Link>.
        </Typography>
      </Alert>

      {/* 💬 Vraag in gewone taal */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: '#f8fafc' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <ChatBubbleOutlineIcon sx={{ fontSize: 18, color: '#0f766e' }} />
          <TextField fullWidth size="small" placeholder='Vraag maar: "wie kan 12u montage doen voor De Groene Weg vóór vrijdag?"'
            value={chatInput} onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); askChat(); } }} />
          <Button variant="contained" onClick={askChat} disabled={chatBusy || !chatInput.trim()}
            startIcon={chatBusy ? <CircularProgress size={14} color="inherit" /> : <SendIcon fontSize="small" />}
            sx={{ minWidth: 44, px: 1.5, bgcolor: '#0f766e', '&:hover': { bgcolor: '#0d5f58' } }} />
        </Stack>
        {chatReply && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, pl: 3.5 }}>{chatReply}</Typography>}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2.5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-end' }} flexWrap="wrap" useFlexGap>
          <TextField select label="Functie" value={role} onChange={(e) => setRole(e.target.value)}
            sx={{ minWidth: 160 }} size="small"
            SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 320 } } } }}>
            {COMMON_ROLES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </TextField>
          <TextField type="number" label="Uren (optioneel)" size="small" sx={{ width: 130 }}
            value={hours} onChange={(e) => setHours(e.target.value)} inputProps={{ min: 0, step: 1 }} />
          <TextField type="date" label="Deadline (optioneel)" size="small" InputLabelProps={{ shrink: true }}
            value={deadline} onChange={(e) => setDeadline(e.target.value)} />
          <Autocomplete size="small" sx={{ minWidth: 220, flex: 1 }} options={projectOptions} value={project}
            getOptionLabel={(o) => o?.label || ''} isOptionEqualToValue={(o, v) => String(o.id) === String(v?.id)}
            loading={projectSearching} filterOptions={(x) => x}
            onChange={(_e, v) => setProject(v)}
            onInputChange={(_e, v, reason) => { if (reason === 'input') searchProjects(v); }}
            renderInput={(params) => <TextField {...params} label="Project (optioneel)" placeholder="zoek project…" />} />
          <Button variant="contained" onClick={() => search()} disabled={loading}
            startIcon={loading ? <CircularProgress size={15} color="inherit" /> : null}
            sx={{ textTransform: 'none', fontWeight: 700, height: 40 }}>
            {Number(hours) > 0 ? 'Verdeel' : 'Zoek kandidaten'}
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {result && result.candidates.length === 0 && <Alert severity="info">Geen medewerkers met deze functie gevonden.</Alert>}

      {/* Verdelingsvoorstel */}
      {distribution && result.candidates.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            <Typography sx={{ fontWeight: 800 }}>
              Voorstel: {result.totalHours}u verdelen{deadline ? ` vóór ${deadline}` : ' (2 weken)'}
              {result.project && <Box component="span" sx={{ fontWeight: 600, color: '#0f766e' }}> · voor {result.project.label?.split(' — ')[0] || result.project.number}</Box>}
            </Typography>
            <FormControlLabel
              control={<Switch size="small" checked={useFlexible} onChange={(e) => setUseFlexible(e.target.checked)} />}
              label={<Typography variant="caption">verschuifbare taken meenemen</Typography>}
            />
          </Stack>
          {distribution.alloc.length === 0 ? (
            <Alert severity="warning">Niemand met deze functie heeft {useFlexible ? '' : 'vrije '}capaciteit in deze periode.</Alert>
          ) : (
            <Stack spacing={1}>
              {distribution.alloc.map((c) => (
                <Stack key={c.id} direction="row" alignItems="center" spacing={1}>
                  <Typography sx={{ fontWeight: 700, flex: 1 }}>{c.name || c.firstName}</Typography>
                  {c.needsShift && <Chip size="small" label="mits verschuiven" sx={{ height: 18, fontSize: 10, bgcolor: '#fef3c7', color: '#92400e' }} />}
                  <Chip label={`${c.assigned}u`} size="small" sx={{ fontWeight: 800, bgcolor: '#dcfce7', color: '#166534' }} />
                </Stack>
              ))}
            </Stack>
          )}
          {distribution.shortfall > 0 && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              {distribution.shortfall}u past niet{useFlexible ? '' : ' (ook niet met verschuiven aan?)'} — overweeg een latere deadline of een freelancer.
            </Alert>
          )}
        </Paper>
      )}

      {/* Kandidaten — klikbaar voor details */}
      {result && result.candidates.length > 0 && (
        <>
          {distribution && <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'text.secondary' }}>Beschikbaarheid per persoon</Typography>}
          <Stack spacing={1}>
            {result.candidates.map((c, i) => {
              const isExp = expandedId === c.id;
              const td = tasksById[c.id];
              return (
                <Paper key={c.id} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', bgcolor: i === 0 && !distribution ? '#f0fdf4' : undefined }}>
                  <Box onClick={() => toggleExpand(c)} sx={{ p: 1.5, cursor: 'pointer' }}>
                    <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                      {isExp ? <ExpandMoreIcon sx={{ fontSize: 18, color: '#6b7280' }} /> : <ChevronRightIcon sx={{ fontSize: 18, color: '#6b7280' }} />}
                      {i === 0 && !distribution && <Chip size="small" label="beste match" sx={{ height: 20, fontSize: 10.5, fontWeight: 800, bgcolor: '#dcfce7', color: '#166534' }} />}
                      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{c.name || c.firstName}</Typography>
                      <Box sx={{ flexGrow: 1 }} />
                      <Typography variant="caption" sx={{ whiteSpace: 'nowrap', fontWeight: 700, color: c.availableHours > 0 ? '#16a34a' : '#ef4444' }}>
                        {c.availableHours > 0 ? `nog ${c.availableHours}u ruimte` : `${Math.abs(c.availableHours)}u óver vol`}
                        {c.flexibleHours > 0 && <Box component="span" sx={{ color: '#92400e', fontWeight: 600 }}> · +{c.flexibleHours}u verschuifbaar</Box>}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 62 }}>belasting</Typography>
                      <LinearProgress variant="determinate" value={Math.min(100, c.capacityPercentage)}
                        sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: '#eef2f7', '& .MuiLinearProgress-bar': { bgcolor: capColor(c.capacityPercentage) } }} />
                      <Typography variant="caption" sx={{ fontWeight: 700, color: capColor(c.capacityPercentage), minWidth: 40, textAlign: 'right' }}>{c.capacityPercentage}%</Typography>
                    </Stack>
                  </Box>
                  <Collapse in={isExp}>
                    <Divider />
                    <Box sx={{ p: 1.5, bgcolor: '#fafbfc' }}>
                      {td?.loading ? (
                        <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={14} /><Typography variant="caption" color="text.secondary">Taken laden…</Typography></Stack>
                      ) : td?.error ? (
                        <Typography variant="caption" color="error">{td.error}</Typography>
                      ) : (td?.tasks || []).filter((t) => !NON_WORK.has(t.type)).length === 0 ? (
                        <Typography variant="caption" color="text.secondary">Geen ingeplande taken in deze periode.</Typography>
                      ) : (
                        <Stack spacing={0.5}>
                          {(td?.tasks || []).filter((t) => !NON_WORK.has(t.type)).map((t) => {
                            const flex = isFlexibleTask(t, result.endDate);
                            return (
                              <Stack key={t.id} direction="row" alignItems="center" spacing={1} sx={{ fontSize: 12.5 }}>
                                <Typography variant="caption" sx={{ flex: 1, fontWeight: 600 }}>{t.name}</Typography>
                                {t.hours > 0 && <Typography variant="caption" color="text.secondary">{t.hours}u</Typography>}
                                {t.deadline
                                  ? <Chip size="small" label={`deadline ${t.deadline}`} sx={{ height: 18, fontSize: 10, bgcolor: '#fee2e2', color: '#b91c1c' }} />
                                  : <Chip size="small" label="geen deadline" variant="outlined" sx={{ height: 18, fontSize: 10, color: '#94a3b8' }} />}
                                {flex && <Chip size="small" label="verschuifbaar" sx={{ height: 18, fontSize: 10, bgcolor: '#fef3c7', color: '#92400e' }} />}
                              </Stack>
                            );
                          })}
                        </Stack>
                      )}
                    </Box>
                  </Collapse>
                </Paper>
              );
            })}
          </Stack>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5 }}>
            Ruimte = 8u/werkdag min geplande taken + echte agenda-meetings, tot de deadline. "Verschuifbaar" = taken zonder harde deadline in deze periode (niet Prio 1).
          </Typography>
        </>
      )}
    </Box>
  );
}
